import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { toast } from "sonner";
import { Mail, RefreshCw, Unplug, Sparkles, AlertTriangle, Bug, Lightbulb, LifeBuoy, Briefcase, Inbox, Building2, ListPlus, CheckCircle2 } from "lucide-react";
import { TaskDialog, localDate } from "./CSMTasks";
import { GmailWizard } from "./GmailWizard";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAT_STYLE = {
  feature_request: { label: "Feature request", icon: Lightbulb, cls: "bg-violet-50 text-violet-700 border-violet-200" },
  bug: { label: "Bug", icon: Bug, cls: "bg-red-50 text-red-700 border-red-200" },
  support: { label: "Supporto", icon: LifeBuoy, cls: "bg-sky-50 text-sky-700 border-sky-200" },
  commercial: { label: "Commerciale", icon: Briefcase, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  other: { label: "Altro", icon: Inbox, cls: "bg-slate-50 text-slate-600 border-slate-200" },
};
const CAT_TO_TASK = { feature_request: "feature", bug: "bug", support: "support", commercial: "commercial", other: "support" };
const MATCH_LABEL = { email: "referente", domain: "dominio", ai: "AI", manual: "manuale", none: "" };
const SENTIMENT_DOT = { positive: "bg-emerald-500", neutral: "bg-slate-300", negative: "bg-red-500" };

const GmailConnectCard = ({ onConnected, userEmail }) => {
  const [busy, setBusy] = useState(false);
  const demo = async () => {
    setBusy(true);
    try {
      await axios.get(`${API}/csm/gmail/connect`);
      toast.success("Modalità DEMO attivata con email di esempio");
      onConnected();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore collegamento");
    }
    setBusy(false);
  };
  return (
    <Card className="border-border" data-testid="gmail-connect-card">
      <CardContent className="py-8 px-8 max-w-2xl mx-auto space-y-6">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-full bg-red-50 flex items-center justify-center shrink-0"><Mail className="h-6 w-6 text-red-500" /></div>
          <div>
            <p className="font-semibold">Collega la tua Gmail in 3 passaggi</p>
            <p className="text-sm text-muted-foreground mt-1">Importiamo solo le email scambiate con i referenti e i domini dei clienti del CRM, l'AI le classifica, e i tuoi riepiloghi partono dalla tua casella.</p>
          </div>
        </div>
        <GmailWizard defaultEmail={userEmail} onConnected={onConnected} />
        <button onClick={demo} disabled={busy} className="text-xs text-muted-foreground underline hover:text-foreground" data-testid="gmail-demo-btn">
          Oppure prova con email di esempio (demo)
        </button>
      </CardContent>
    </Card>
  );
};

const CategoryFilter = ({ counts, value, onChange }) => {
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  const items = [["", "Tutte", total], ...Object.entries(CAT_STYLE).map(([k, v]) => [k, v.label, counts[k] || 0])];
  return (
    <div className="flex flex-wrap gap-2" data-testid="email-category-filter">
      {items.map(([k, label, n]) => (
        <button key={k || "all"} onClick={() => onChange(k)} data-testid={`email-cat-${k || "all"}`}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${value === k ? "bg-foreground text-background border-foreground" : "bg-background hover:bg-muted border-border"}`}>
          {label} <span className="opacity-60 ml-1">{n}</span>
        </button>
      ))}
    </div>
  );
};

const EmailRow = ({ email, clients, onUpdate, onCreateTask }) => {
  const cat = CAT_STYLE[email.category] || CAT_STYLE.other;
  return (
    <div className="px-4 py-3 border-t border-border/40 hover:bg-muted/20 transition-colors" data-testid={`email-row-${email.email_id}`}>
      <div className="flex items-start gap-3">
        <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${SENTIMENT_DOT[email.sentiment] || SENTIMENT_DOT.neutral}`} title={email.sentiment} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm truncate">{email.subject || "(senza oggetto)"}</span>
            {email.urgent && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-red-600 uppercase" data-testid="email-urgent-badge"><AlertTriangle className="h-3 w-3" /> Urgente</span>}
          </div>
          <p className="text-xs text-muted-foreground truncate">{email.from} · {new Date(email.date).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}</p>
          {email.summary && <p className="text-sm mt-1 flex items-start gap-1.5"><Sparkles className="h-3.5 w-3.5 mt-0.5 text-violet-500 shrink-0" />{email.summary}</p>}
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <select value={email.category} onChange={(e) => onUpdate(email.email_id, { category: e.target.value })} data-testid={`email-category-select-${email.email_id}`}
            className={`h-7 rounded-full border px-2 text-xs font-medium ${cat.cls}`}>
            {Object.entries(CAT_STYLE).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <div className="flex items-center gap-1">
            <Building2 className="h-3 w-3 text-muted-foreground" />
            <select value={email.client_id || ""} onChange={(e) => onUpdate(email.email_id, { client_id: e.target.value })} data-testid={`email-client-select-${email.email_id}`}
              className="h-7 max-w-[200px] rounded-md border border-input bg-background px-2 text-xs">
              <option value="">— Nessun cliente —</option>
              {clients.map(c => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
            </select>
          </div>
          {MATCH_LABEL[email.match_method] && <span className="text-[10px] text-muted-foreground">abbinato via {MATCH_LABEL[email.match_method]}</span>}
          {email.task_id ? (
            <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 font-medium" data-testid={`email-task-created-${email.email_id}`}><CheckCircle2 className="h-3 w-3" /> Task creato</span>
          ) : (
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => onCreateTask(email)} data-testid={`email-create-task-${email.email_id}`}><ListPlus className="h-3.5 w-3.5" /> Crea Task</Button>
          )}
        </div>
      </div>
    </div>
  );
};

export const CSMEmails = ({ clients }) => {
  const [status, setStatus] = useState(null);
  const [data, setData] = useState({ emails: [], counts: {} });
  const [category, setCategory] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [taskDraft, setTaskDraft] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);

  const loadStatus = useCallback(async () => {
    const res = await axios.get(`${API}/csm/gmail/status`);
    setStatus(res.data);
    return res.data;
  }, []);

  const loadEmails = useCallback(async () => {
    const res = await axios.get(`${API}/csm/emails`, { params: { category, client_id: clientFilter } });
    setData(res.data);
  }, [category, clientFilter]);

  const sync = useCallback(async (silent = false) => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/csm/gmail/sync`);
      if (!silent) toast.success(`Sincronizzate ${res.data.new} nuove email`);
      await loadStatus();
      await loadEmails();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore sincronizzazione");
    }
    setSyncing(false);
  }, [loadStatus, loadEmails]);

  useEffect(() => {
    loadStatus().then(s => {
      const last = s.connection?.last_sync;
      if (s.connection && (!last || Date.now() - new Date(last).getTime() > 3600000)) sync(true);
    }).catch(() => toast.error("Errore caricamento Gmail"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (status?.connection) loadEmails(); }, [status?.connection, loadEmails]);

  const updateEmail = async (id, patch) => {
    try {
      await axios.put(`${API}/csm/emails/${id}`, patch);
      loadEmails();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore aggiornamento");
    }
  };

  const openTask = (e) => {
    const due = localDate(new Date(Date.now() + (e.urgent ? 1 : 7) * 86400000));
    setTaskDraft({ title: e.summary || e.subject || "", type: CAT_TO_TASK[e.category] || "support", client_id: e.client_id || "", due_date: due,
      description: `Da email: ${e.subject}\n${e.from}`, email_id: e.email_id, send_to_tracker: ["bug", "feature_request"].includes(e.category) });
  };

  const disconnect = async () => {
    if (!window.confirm("Scollegare Gmail? Le email importate verranno rimosse.")) return;
    try {
      await axios.post(`${API}/csm/gmail/disconnect`);
      setData({ emails: [], counts: {} });
      loadStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore scollegamento");
    }
  };

  if (!status) return <div className="py-12 flex justify-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" /></div>;
  if (!status.connection) return <GmailConnectCard userEmail={status.user_email} onConnected={() => loadStatus().then(() => sync())} />;

  const conn = status.connection;
  return (
    <div className="space-y-4" data-testid="csm-emails">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 text-sm">
          <Mail className="h-4 w-4 text-red-500" />
          <span className="font-medium" data-testid="gmail-connected-email">{conn.gmail_email}</span>
          {conn.mode === "demo" && <span className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded bg-amber-100 text-amber-800" data-testid="gmail-demo-badge">Demo</span>}
          {conn.mode === "demo" && <button className="text-xs text-primary underline" onClick={() => setWizardOpen(true)} data-testid="gmail-connect-real-btn">Collega la Gmail vera</button>}
          <span className="text-xs text-muted-foreground">· ultima sync {conn.last_sync ? new Date(conn.last_sync).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "mai"}</span>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="h-9 gap-2 text-sm" onClick={() => sync()} disabled={syncing} data-testid="gmail-sync-btn">
            <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Analisi AI in corso..." : "Sincronizza"}
          </Button>
          <Button variant="ghost" className="h-9 gap-2 text-sm text-muted-foreground" onClick={disconnect} data-testid="gmail-disconnect-btn">
            <Unplug className="h-4 w-4" /> Scollega
          </Button>
        </div>
      </div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <CategoryFilter counts={data.counts} value={category} onChange={setCategory} />
        <select className="h-9 rounded-md border border-input bg-background px-3 text-sm" value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} data-testid="email-client-filter">
          <option value="">Tutti i clienti</option>
          <option value="unmatched">Non abbinate</option>
          {clients.map(c => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
        </select>
      </div>
      <div className="rounded-lg border border-border/60 overflow-hidden">
        {data.emails.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-muted-foreground" data-testid="emails-empty">Nessuna email trovata</p>
        ) : data.emails.map(e => <EmailRow key={e.email_id} email={e} clients={clients} onUpdate={updateEmail} onCreateTask={openTask} />)}
        <div className="px-4 py-2 bg-muted/30 border-t border-border/40 text-xs text-muted-foreground" data-testid="emails-count">{data.emails.length} email</div>
      </div>
      <Dialog open={wizardOpen} onOpenChange={setWizardOpen}>
        <DialogContent className="sm:max-w-2xl" data-testid="gmail-wizard-dialog">
          <DialogHeader><DialogTitle>Collega la tua Gmail</DialogTitle></DialogHeader>
          <GmailWizard defaultEmail={status.user_email} onConnected={async () => {
            setWizardOpen(false);
            setData({ emails: [], counts: {} });
            await loadStatus();
            sync();
          }} />
        </DialogContent>
      </Dialog>
      <TaskDialog open={!!taskDraft} onClose={(v) => !v && setTaskDraft(null)} initial={taskDraft} clients={clients} onSaved={loadEmails} />
    </div>
  );
};
